import { useMemo, useState, useEffect } from 'react';
import { useList } from '@refinedev/core';
import { Filter } from 'lucide-react';
import { OrderTargetType } from '@masters-club/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import type { AdminUser } from './types';

export interface OrdersFilterValues {
  search?: string;
  assignedAdminId?: number;
  targetType?: OrderTargetType;
  targetId?: number;
  pricingTierId?: number;
  totalFrom?: number;
  totalTo?: number;
  createdFrom?: string;
  createdTo?: string;
  currency?: string;
}

// --- Translations-aware label resolution ---
// Resources like courses / subscription-plans / channel-passes / pricing-tiers
// expose a `translations` map keyed by language code. For the Kanban filter
// dropdown we always use Russian with a sensible fallback.

interface TranslationsByLang {
  ru?: { name?: string | null; title?: string | null };
}

interface TranslationsArray {
  languageCode: string;
  name?: string | null;
  title?: string | null;
  data?: { name?: string; title?: string };
}

interface CourseOption {
  id: number;
  slug: string;
  translations?: Array<TranslationsArray & { title?: string | null }>;
}

interface SubscriptionPlanOption {
  id: number;
  code: string;
  translations?: TranslationsByLang;
}

interface ChannelPassOption {
  id: number;
  translations?: TranslationsByLang;
  channel?: { title: string } | null;
}

interface PricingTierOption {
  id: number;
  order: number;
  courseId: number | null;
  subscriptionPlanId: number | null;
  channelPassId: number | null;
  translations?: TranslationsByLang | Array<TranslationsArray>;
}

function resolveCourseLabel(item: CourseOption): string {
  const translations = item.translations ?? [];
  const ruTranslation = translations.find((t) => t.languageCode === 'ru');
  const title = ruTranslation?.title ?? ruTranslation?.data?.title;
  if (title) { return title; }
  return item.slug || `#${item.id}`;
}

function resolveSubscriptionPlanLabel(item: SubscriptionPlanOption): string {
  const name = item.translations?.ru?.name;
  if (name) { return name; }
  return item.code || `#${item.id}`;
}

function resolveChannelPassLabel(item: ChannelPassOption): string {
  const name = item.translations?.ru?.name;
  if (name) { return name; }
  if (item.channel?.title) { return item.channel.title; }
  return `#${item.id}`;
}

function resolvePricingTierLabel(item: PricingTierOption): string {
  const translations = item.translations;
  let name: string | undefined | null;
  if (Array.isArray(translations)) {
    const ru = translations.find((t) => t.languageCode === 'ru');
    name = ru?.name ?? ru?.data?.name;
  } else if (translations && typeof translations === 'object') {
    name = translations.ru?.name;
  }
  if (name) { return name; }
  return `Тариф #${item.id}`;
}

// --- Product (targetId) dropdown: depends on targetType ---

interface ProductSelectProps {
  targetType: OrderTargetType | undefined;
  value: number | undefined;
  onChange: (id: number | undefined) => void;
}

function ProductSelect({ targetType, value, onChange }: ProductSelectProps) {
  const isCourse = targetType === OrderTargetType.COURSE;
  const isPlan = targetType === OrderTargetType.SUBSCRIPTION_PLAN;
  const isPass = targetType === OrderTargetType.CHANNEL_PASS;

  const coursesResult = useList<CourseOption>({
    resource: 'courses',
    pagination: { pageSize: 200 },
    queryOptions: { enabled: isCourse, staleTime: 60_000 },
  });
  const plansResult = useList<SubscriptionPlanOption>({
    resource: 'subscription-plans',
    pagination: { pageSize: 200 },
    queryOptions: { enabled: isPlan, staleTime: 60_000 },
  });
  const passesResult = useList<ChannelPassOption>({
    resource: 'channel-passes',
    pagination: { pageSize: 200 },
    queryOptions: { enabled: isPass, staleTime: 60_000 },
  });

  let activeResult: typeof coursesResult | typeof plansResult | typeof passesResult | null = null;
  if (isCourse) { activeResult = coursesResult; }
  else if (isPlan) { activeResult = plansResult; }
  else if (isPass) { activeResult = passesResult; }
  const isLoading = activeResult?.query?.isLoading ?? false;

  const options = useMemo(() => {
    if (isCourse) {
      return (coursesResult.result?.data ?? []).map((c) => ({ id: c.id, label: resolveCourseLabel(c) }));
    }
    if (isPlan) {
      return (plansResult.result?.data ?? []).map((p) => ({ id: p.id, label: resolveSubscriptionPlanLabel(p) }));
    }
    if (isPass) {
      return (passesResult.result?.data ?? []).map((cp) => ({ id: cp.id, label: resolveChannelPassLabel(cp) }));
    }
    return [];
  }, [isCourse, isPlan, isPass, coursesResult.result, plansResult.result, passesResult.result]);

  const disabled = !isCourse && !isPlan && !isPass;
  const placeholder = disabled
    ? 'Сначала выберите тип товара'
    : (isLoading ? 'Загрузка...' : 'Любой товар');

  return (
    <Select
      disabled={disabled}
      value={value !== undefined ? String(value) : '__all__'}
      onValueChange={(v) => onChange(v === '__all__' ? undefined : Number(v))}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">Любой товар</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.id} value={String(opt.id)}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// --- Pricing tier dropdown ---

interface PricingTierSelectProps {
  targetType: OrderTargetType | undefined;
  targetId: number | undefined;
  value: number | undefined;
  onChange: (id: number | undefined) => void;
}

function PricingTierSelect({ targetType, targetId, value, onChange }: PricingTierSelectProps) {
  // Load all active tiers (pagination off not needed — 200 is plenty for admin)
  const { result, query } = useList<PricingTierOption>({
    resource: 'pricing-tiers',
    pagination: { pageSize: 200 },
    queryOptions: { staleTime: 60_000 },
  });

  const tiersData = result?.data;
  const filteredTiers = useMemo(() => {
    const tiers = tiersData ?? [];
    if (!targetType) { return tiers; }
    return tiers.filter((tier) => {
      if (targetType === OrderTargetType.COURSE) {
        return tier.courseId !== null && (targetId === undefined || tier.courseId === targetId);
      }
      if (targetType === OrderTargetType.SUBSCRIPTION_PLAN) {
        return tier.subscriptionPlanId !== null && (targetId === undefined || tier.subscriptionPlanId === targetId);
      }
      if (targetType === OrderTargetType.CHANNEL_PASS) {
        return tier.channelPassId !== null && (targetId === undefined || tier.channelPassId === targetId);
      }
      return false;
    });
  }, [tiersData, targetType, targetId]);

  const isLoading = query?.isLoading ?? false;

  return (
    <Select
      value={value !== undefined ? String(value) : '__all__'}
      onValueChange={(v) => onChange(v === '__all__' ? undefined : Number(v))}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={isLoading ? 'Загрузка...' : 'Любой тариф'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">Любой тариф</SelectItem>
        {filteredTiers.map((tier) => (
          <SelectItem key={tier.id} value={String(tier.id)}>
            {resolvePricingTierLabel(tier)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// --- Main toolbar ---

const TARGET_TYPE_OPTIONS: Array<{ value: OrderTargetType; label: string }> = [
  { value: OrderTargetType.COURSE, label: 'Курс' },
  { value: OrderTargetType.SUBSCRIPTION_PLAN, label: 'Абонемент' },
  { value: OrderTargetType.CHANNEL_PASS, label: 'Доступ к каналу' },
  { value: OrderTargetType.BONUS, label: 'Привилегия' },
];

function countActive(values: OrdersFilterValues): number {
  let n = 0;
  if (values.search) { n += 1; }
  if (values.assignedAdminId !== undefined) { n += 1; }
  if (values.targetType) { n += 1; }
  if (values.targetId !== undefined) { n += 1; }
  if (values.pricingTierId !== undefined) { n += 1; }
  if (values.totalFrom !== undefined || values.totalTo !== undefined) { n += 1; }
  if (values.createdFrom || values.createdTo) { n += 1; }
  if (values.currency) { n += 1; }
  return n;
}

interface OrdersFiltersToolbarProps {
  values: OrdersFilterValues;
  onChange: (values: OrdersFilterValues) => void;
  admins: AdminUser[];
  availableCurrencies?: string[];
  onSaveAsDefaults?: () => void;
}

export function OrdersFiltersToolbar({
  values,
  onChange,
  admins,
  availableCurrencies = [],
  onSaveAsDefaults,
}: OrdersFiltersToolbarProps) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState<OrdersFilterValues>(values);

  // Reset local state to committed values when dialog opens
  useEffect(() => {
    if (open) { setLocal(values); }
  }, [open, values]);

  const activeCount = countActive(values);

  const handleApply = () => {
    onChange(local);
    setOpen(false);
  };

  const handleReset = () => {
    const empty: OrdersFilterValues = {};
    setLocal(empty);
    onChange(empty);
    setOpen(false);
  };

  // When targetType changes, clear targetId + pricingTierId if они не соответствуют
  const setTargetType = (next: OrderTargetType | undefined) => {
    setLocal((prev) => {
      if (prev.targetType === next) { return prev; }
      return { ...prev, targetType: next, targetId: undefined, pricingTierId: undefined };
    });
  };

  const setTargetId = (next: number | undefined) => {
    setLocal((prev) => ({ ...prev, targetId: next, pricingTierId: undefined }));
  };

  const dateRange = local.createdFrom || local.createdTo
    ? {
        from: local.createdFrom ? new Date(local.createdFrom) : undefined,
        to: local.createdTo ? new Date(local.createdTo) : undefined,
      }
    : null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Filter className="h-4 w-4" />
            <span>Фильтры</span>
            {activeCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5">
                {activeCount}
              </Badge>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Фильтры заказов</DialogTitle>
          </DialogHeader>

          <div className="grid gap-5 py-2">
            {/* Currency */}
            {availableCurrencies.length > 0 && (
              <div className="space-y-2">
                <Label>Валюта</Label>
                <Select
                  value={local.currency ?? '__all__'}
                  onValueChange={(v) => setLocal((p) => ({ ...p, currency: v === '__all__' ? undefined : v }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Все валюты" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Все валюты</SelectItem>
                    {availableCurrencies.map((cur) => (
                      <SelectItem key={cur} value={cur}>{cur}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Определяет валюту агрегатов в колонках Kanban
                </p>
              </div>
            )}

            {/* Product type */}
            <div className="space-y-2">
              <Label>Тип товара</Label>
              <Select
                value={local.targetType ?? '__all__'}
                onValueChange={(v) => setTargetType(v === '__all__' ? undefined : (v as OrderTargetType))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Любой тип" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Любой тип</SelectItem>
                  {TARGET_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Product */}
            {local.targetType && local.targetType !== OrderTargetType.BONUS && (
              <div className="space-y-2">
                <Label>Товар</Label>
                <ProductSelect
                  targetType={local.targetType}
                  value={local.targetId}
                  onChange={setTargetId}
                />
              </div>
            )}

            {/* Pricing tier */}
            {local.targetType && local.targetType !== OrderTargetType.BONUS && (
              <div className="space-y-2">
                <Label>Тариф</Label>
                <PricingTierSelect
                  targetType={local.targetType}
                  targetId={local.targetId}
                  value={local.pricingTierId}
                  onChange={(v) => setLocal((p) => ({ ...p, pricingTierId: v }))}
                />
              </div>
            )}

            {/* Assigned admin */}
            <div className="space-y-2">
              <Label>Назначенный администратор</Label>
              <Select
                value={local.assignedAdminId !== undefined ? String(local.assignedAdminId) : '__all__'}
                onValueChange={(v) => setLocal((p) => ({
                  ...p,
                  assignedAdminId: v === '__all__' ? undefined : Number(v),
                }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Любой администратор" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Любой администратор</SelectItem>
                  {admins.map((admin) => (
                    <SelectItem key={admin.id} value={String(admin.id)}>
                      {admin.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount range */}
            <div className="space-y-2">
              <Label>Сумма</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="От"
                  value={local.totalFrom ?? ''}
                  onChange={(e) => setLocal((p) => ({
                    ...p,
                    totalFrom: e.target.value === '' ? undefined : Number(e.target.value),
                  }))}
                  className="flex-1"
                />
                <span className="text-muted-foreground text-sm shrink-0">—</span>
                <Input
                  type="number"
                  placeholder="До"
                  value={local.totalTo ?? ''}
                  onChange={(e) => setLocal((p) => ({
                    ...p,
                    totalTo: e.target.value === '' ? undefined : Number(e.target.value),
                  }))}
                  className="flex-1"
                />
              </div>
            </div>

            {/* Date range */}
            <div className="space-y-2">
              <Label>Дата создания</Label>
              <DateRangePicker
                value={dateRange}
                onChange={(range) => setLocal((p) => ({
                  ...p,
                  createdFrom: range?.from ? range.from.toISOString() : undefined,
                  createdTo: range?.to ? range.to.toISOString() : undefined,
                }))}
              />
            </div>
          </div>

          <DialogFooter className="flex-col gap-3 sm:flex-col sm:items-stretch">
            {onSaveAsDefaults && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground text-xs"
                onClick={() => {
                  onChange(local);
                  onSaveAsDefaults();
                }}
              >
                Сохранить текущие фильтры как мои по умолчанию
              </Button>
            )}
            <div className="flex items-center justify-between gap-2 w-full">
              <Button variant="outline" onClick={handleReset}>
                Сбросить
              </Button>
              <div className="flex gap-2">
                <DialogClose asChild>
                  <Button variant="outline">Отмена</Button>
                </DialogClose>
                <Button onClick={handleApply}>Применить</Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {activeCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          className="text-muted-foreground"
        >
          Сбросить все
        </Button>
      )}
    </div>
  );
}
